import React from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';

/**
 * EvolutionChart — graphique barres masse salariale mensuelle (24 mois).
 * Valeur (en k CHF) affichée AU CENTRE de chaque barre ; barres alignées à
 * gauche (sous le titre) ; ligne EPT sous le graphique avec titre au-dessus.
 * Pur présentationnel : reçoit les séries déjà calculées.
 */
export default function EvolutionChart({ series24, series24fte, axisMin, axisMax, prevYear, refYear }) {
  const { t } = useTranslation();
  const p = useP();
  const axisRange = (axisMax - axisMin) || 1;
  const barPct = (v) => Math.max(2, ((v - axisMin) / axisRange) * 100);
  const monthLabel = (idx) => t(`common:months.${idx + 1}`);

  return (
    <div className="hr-chart">
      <div className="hr-chart__title">
        {p('MASSE SALARIALE MENSUELLE · ')}{prevYear} → {refYear}
        <span style={{ marginLeft: 12, color: 'var(--color-text-muted)', fontSize: 10, textTransform: 'none', letterSpacing: 0 }}>
          {p('brut mensuel lissé (13e/14e et compléments répartis) · 24 mois')}
        </span>
      </div>

      <div className="hr-chart__bars" style={{ gridTemplateColumns: 'repeat(24, 1fr)', gap: 3 }}>
        {series24.map((v, i) => {
          const isCurrentYear = i >= 12;
          const monthIdx = i % 12;
          const year = isCurrentYear ? refYear : prevYear;
          return (
            <div className="hr-chart__col" key={i}>
              <div className="hr-chart__bar-wrap">
                <div
                  className="hr-chart__bar"
                  style={{
                    height: `${barPct(v)}%`,
                    background: isCurrentYear
                      ? 'var(--color-accent)'
                      : 'var(--color-accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={`${monthLabel(monthIdx)} ${year} — ${formatNumber(v, 0)} CHF`}
                >
                  <span style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 8.5,
                    fontWeight: 600,
                    color: '#2B203E',
                    whiteSpace: 'nowrap',
                  }}>
                    {formatNumber(v / 1000, 0)}
                  </span>
                </div>
              </div>
              <div className="hr-chart__month" style={{ fontSize: 9 }}>
                {monthLabel(monthIdx)}
                <div style={{ fontSize: 8, color: 'var(--color-text-muted)', minHeight: 10 }}>
                  {monthIdx === 0 ? year : ' '}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hr-chart__legend">
        <span><span className="hr-chart__sw" style={{ background: 'var(--color-accent-soft)' }} />{prevYear}</span>
        <span><span className="hr-chart__sw" style={{ background: 'var(--color-accent)' }} />{refYear}</span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 9, marginLeft: 12 }}>
          {p('valeurs en k CHF · axe Y zoomé · base ')}{formatNumber(axisMin, 0)} → {formatNumber(axisMax, 0)}
        </span>
      </div>

      {/* EPT : titre au-dessus des valeurs */}
      <div style={{ marginTop: 14 }}>
        <div style={{
          fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4,
        }}>
          EPT
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 3 }}>
          {series24fte.map((v, i) => (
            <div key={i} style={{
              textAlign: 'center', color: 'var(--color-text)',
              fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums',
              fontSize: 9, lineHeight: '16px',
            }}>
              {formatNumber(v, 1)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
